// QR codes for inventory containers, drawn in the browser and saved as PNG files.
//
// The sticker on a first aid bag encodes a public URL that lists what should be in it
// (see backend core/kit_qr.py — the backend mints and stores the token, and serves the
// list; nothing but the picture is made here). Drawn client-side because that is where
// every other file this app produces is made, and because a PNG generated from a token
// we already hold needs no round trip.
//
// Both dependencies are loaded on demand, as jsPDF is: neither belongs in the main
// bundle for the sake of a button most users never press.

import { downloadBlob, fileSlug } from "./download";

// Deliberately large for something that will be printed and then live in a bag:
// 1024px prints crisply on a sticker at any size anyone would use.
const SIZE = 1024;
// The quiet zone, in modules. Four is the spec's minimum, and scanners need it —
// a code cropped flush to the edge is unreliable.
const MARGIN = 4;
// "Q" recovers from ~25% damage against "M"'s ~15%. These get handled with wet gloves,
// scuffed against everything else in the bag, and stuck on curved nylon, so the extra
// modules are worth paying for.
const ERROR_CORRECTION = "Q";

/** A QR code for `url` as a PNG blob. */
export const qrPngBlob = async (url) => {
  const QRCode = (await import("qrcode")).default;
  const canvas = document.createElement("canvas");
  await QRCode.toCanvas(canvas, url, {
    width: SIZE,
    margin: MARGIN,
    errorCorrectionLevel: ERROR_CORRECTION,
    // White rather than transparent: a transparent PNG printed onto anything but white
    // paper, or viewed in a dark-mode previewer, stops scanning.
    color: { dark: "#000000ff", light: "#ffffffff" },
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => (blob ? resolve(blob) : reject(new Error("Could not draw the QR code."))),
      "image/png",
    );
  });
};

// One code's filename. The name is the container's, so a folder of stickers can be
// matched to the bags they go on without opening them.
const codeFilename = (name) => `${fileSlug(name, "container")}-qr.png`;

/** Download a single container's QR code. */
export const downloadQrCode = async ({ name, url }) => {
  downloadBlob(await qrPngBlob(url), codeFilename(name));
};

/**
 * Download a whole kit's codes as one zip of named PNGs — fourteen stickers for
 * fourteen first aid bags in one go.
 *
 * A zip rather than fourteen saves: browsers treat repeated programmatic downloads as
 * something to be prompted about (or blocked), and declining that prompt silently
 * yields nothing. `codes` is the response from POST /inventory/items/{id}/qr-codes.
 */
export const downloadQrCodes = async (codes, zipName) => {
  const JSZip = (await import("jszip")).default;
  const zip = new JSZip();
  const used = new Map();
  for (const code of codes) {
    let filename = codeFilename(code.name);
    // Two containers can be named the same (the distinctness rule allows it as long as
    // they sit in different places), and one would otherwise overwrite the other inside
    // the zip — leaving fewer stickers than bags, which nobody would notice until they
    // came to stick them on.
    const seen = used.get(filename) ?? 0;
    used.set(filename, seen + 1);
    if (seen > 0) filename = filename.replace(/\.png$/, `-${seen + 1}.png`);
    zip.file(filename, await qrPngBlob(code.url));
  }
  const blob = await zip.generateAsync({ type: "blob" });
  downloadBlob(blob, `${fileSlug(zipName, "kit")}-qr-codes.zip`);
};
